import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { CheckCircle } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import 'react-quill/dist/quill.snow.css';
import { resolveSiteLogoUrl } from '../SiteManagement/siteApi';
import {
  buildBankDetails,
  fetchBankDetailsForQuotation,
  buildCustomerFromRecord,
  buildVendorFromSite,
  formatPhoneEmail,
  resolveVendorDisplayName,
} from './quotationPartyHelpers';
import { resolvePocGstin } from './quotationPocHelpers';

const defaultFormatCurrency = (amount = 0) =>
  Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);

const convertImageSourceToDataUrl = async (src) => {
  if (!src || src.startsWith('data:')) {
    return src;
  }

  try {
    const response = await fetch(src);
    if (!response.ok) {
      return src;
    }

    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || src);
      reader.onerror = () => resolve(src);
      reader.readAsDataURL(blob);
    });
  } catch {
    return src;
  }
};

const waitForImageElement = (img, timeoutMs = 5000) =>
  new Promise((resolve) => {
    if (!img?.src) {
      resolve();
      return;
    }

    if (img.complete && img.naturalWidth > 0) {
      resolve();
      return;
    }

    let settled = false;
    const done = () => {
      if (settled) {
        return;
      }
      settled = true;
      resolve();
    };

    img.onload = done;
    img.onerror = done;
    setTimeout(done, timeoutMs);
  });

const PDF_PEACH = '#F9D9C9';
const PDF_BLACK = '#000000';
const PDF_WHITE = '#ffffff';

/* Embedded in each .pdf-page so html2canvas clone includes styles (sibling <style> is not captured). */
const QUOTATION_PDF_EMBEDDED_STYLES = `
  .pdf-page {
    color: #000000;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 9px;
    line-height: 1.25;
    background: #ffffff;
  }
  .pdf-page .pdf-page-inner {
    border: 1px solid #000000;
    box-sizing: border-box;
    min-height: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .pdf-page .pdf-page-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1 1 auto;
  }
  .pdf-page .pdf-page-bottom {
    flex-shrink: 0;
    margin-top: auto;
    width: 100%;
  }
  .pdf-page .q-powered-by-block { text-align: center; padding-top: 8px; }
  .pdf-page .q-powered-by-text {
    margin: 0;
    font-size: 7px;
    font-weight: 400;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #000000;
  }
  .pdf-page .q-powered-by-line {
    display: block;
    width: 100%;
    height: 1px;
    min-height: 1px;
    margin: 8px 0 6px;
    padding: 0;
    border: 0;
    background-color: #000000;
  }
  .pdf-page .pdf-page-footer {
    border-top: 0;
    padding-top: 4px;
    text-align: center;
    font-size: 8px;
    color: #000000;
  }
  .pdf-page .pdf-page-header { border-bottom: 1px solid #000000; }
  .pdf-page .q-box:not(.q-table-frame) { border: 1px solid #000000; border-radius: 0; }
  .pdf-page .q-amount-words { font-weight: 700; color: #000000; font-size: 8px; margin: 0; }
  .pdf-page table.q-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  .pdf-page table.q-table th,
  .pdf-page table.q-table td {
    border: 1px solid #000000;
    padding: 6px;
    font-size: 8px;
    line-height: 1.35;
    color: #000000;
    vertical-align: middle;
    box-sizing: border-box;
    background: #ffffff;
  }
  .pdf-page table.q-table thead th,
  .pdf-page table.q-table td.q-label-cell,
  .pdf-page table.q-table tr.q-summary-label td,
  .pdf-page table.q-table tr.q-total-row td {
    background: #F9D9C9;
    font-weight: 700;
  }
  .pdf-page table.q-table td.q-data-cell { background: #ffffff; font-weight: 400; text-align: left; }
  .pdf-page table.q-table tfoot tr.q-tax-total td { background: #ffffff; font-weight: 700; }
  .pdf-page table.q-table .q-align-left { text-align: left; }
  .pdf-page table.q-table .q-align-right { text-align: right; }
  .pdf-page table.q-table .q-align-center { text-align: center; }
  .pdf-page table.q-table .q-cell-inner {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 14px;
    line-height: 1.35;
    box-sizing: border-box;
  }
  .pdf-page table.q-table .q-align-left .q-cell-inner,
  .pdf-page table.q-table td.q-label-cell .q-cell-inner,
  .pdf-page table.q-table td.q-data-cell .q-cell-inner {
    justify-content: flex-start;
    text-align: left;
  }
  .pdf-page table.q-table .q-align-right .q-cell-inner { justify-content: flex-end; text-align: right; }
  .pdf-page table.q-table .q-align-center .q-cell-inner { justify-content: center; text-align: center; }
  .pdf-page table.q-table th p,
  .pdf-page table.q-table td p { margin: 0; padding: 0; line-height: 1.35; }
`;

const injectQuotationPdfStyles = (clonedDoc) => {
  if (!clonedDoc?.head) {
    return;
  }

  if (clonedDoc.head.querySelector('[data-quotation-pdf-styles]')) {
    return;
  }

  if (typeof document !== 'undefined') {
    const sourceStyle = Array.from(document.querySelectorAll('style')).find((node) =>
      node.textContent?.includes('.pdf-page')
    );
    if (sourceStyle) {
      const clone = sourceStyle.cloneNode(true);
      clone.setAttribute('data-quotation-pdf-styles', 'true');
      clonedDoc.head.appendChild(clone);
      return;
    }
  }
};

const preparePdfPageForCapture = (root) => {
  if (!root) return;

  root.style.fontFamily = 'Arial, Helvetica, sans-serif';
  root.style.color = PDF_BLACK;
  root.style.backgroundColor = PDF_WHITE;
  root.style.boxSizing = 'border-box';

  root.querySelectorAll('.pdf-page-inner').forEach((el) => {
    el.style.border = `1px solid ${PDF_BLACK}`;
    el.style.boxSizing = 'border-box';
    el.style.display = 'flex';
    el.style.flexDirection = 'column';
    el.style.minHeight = '100%';
    el.style.height = '100%';
  });

  root.querySelectorAll('.pdf-page-content').forEach((el) => {
    el.style.flex = '1 1 auto';
    el.style.display = 'flex';
    el.style.flexDirection = 'column';
  });

  root.querySelectorAll('.pdf-page-bottom').forEach((el) => {
    el.style.marginTop = 'auto';
    el.style.width = '100%';
    el.style.flexShrink = '0';
  });

  root.querySelectorAll('.pdf-page-header').forEach((el) => {
    el.style.borderBottom = `1px solid ${PDF_BLACK}`;
  });

  root.querySelectorAll('.pdf-page-footer').forEach((el) => {
    el.style.borderTop = '0';
    el.style.textAlign = 'center';
    el.style.fontSize = '8px';
    el.style.color = PDF_BLACK;
    el.style.paddingTop = '4px';
  });

  root.querySelectorAll('.q-powered-by-line').forEach((el) => {
    el.style.display = 'block';
    el.style.width = '100%';
    el.style.height = '1px';
    el.style.minHeight = '1px';
    el.style.margin = '8px 0 6px';
    el.style.padding = '0';
    el.style.border = '0';
    el.style.backgroundColor = PDF_BLACK;
  });

  root.querySelectorAll('.q-powered-by-text').forEach((el) => {
    el.style.color = PDF_BLACK;
    el.style.margin = '0';
    el.style.fontSize = '7px';
    el.style.letterSpacing = '0.2em';
    el.style.textTransform = 'uppercase';
    el.style.textAlign = 'center';
  });

  root.querySelectorAll('.q-amount-words').forEach((el) => {
    el.style.fontWeight = '700';
    el.style.color = PDF_BLACK;
    el.style.fontSize = '8px';
    el.style.margin = '0';
    el.style.lineHeight = '1.35';
  });

  root.querySelectorAll('.q-box:not(.q-table-frame)').forEach((el) => {
    el.style.border = `1px solid ${PDF_BLACK}`;
    el.style.borderRadius = '0';
  });

  root.querySelectorAll('table.q-table').forEach((table) => {
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.tableLayout = 'fixed';
  });

  root.querySelectorAll('table.q-table th, table.q-table td').forEach((cell) => {
    const row = cell.closest('tr');
    const isHeader = cell.tagName === 'TH';
    const isLabel = cell.classList.contains('q-label-cell');
    const isData = cell.classList.contains('q-data-cell');
    const isSummary = row?.classList.contains('q-summary-label');
    const isTotal = row?.classList.contains('q-total-row');
    const isTaxTotal = row?.classList.contains('q-tax-total');
    const inThead = Boolean(cell.closest('thead'));

    cell.style.border = `1px solid ${PDF_BLACK}`;
    cell.style.boxSizing = 'border-box';
    cell.style.padding = '6px';
    cell.style.verticalAlign = 'middle';
    cell.style.lineHeight = '1.35';
    cell.style.fontSize = '8px';
    cell.style.color = PDF_BLACK;

    if (isHeader || inThead || isLabel || isSummary || isTotal) {
      cell.style.backgroundColor = PDF_PEACH;
      cell.style.fontWeight = '700';
    } else if (isTaxTotal) {
      cell.style.backgroundColor = PDF_WHITE;
      cell.style.fontWeight = '700';
    } else {
      cell.style.backgroundColor = PDF_WHITE;
      cell.style.fontWeight = '400';
    }

    if (cell.classList.contains('q-align-right')) {
      cell.style.textAlign = 'right';
    } else if (cell.classList.contains('q-align-left') || isLabel || isData) {
      cell.style.textAlign = 'left';
    } else {
      cell.style.textAlign = 'center';
    }

    let inner = cell.querySelector('.q-cell-inner');
    if (!inner) {
      inner = cell.ownerDocument.createElement('div');
      inner.className = 'q-cell-inner';
      while (cell.firstChild) {
        inner.appendChild(cell.firstChild);
      }
      cell.appendChild(inner);
    }

    inner.style.display = 'flex';
    inner.style.alignItems = 'center';
    inner.style.width = '100%';
    inner.style.minHeight = '14px';
    inner.style.lineHeight = '1.35';
    inner.style.boxSizing = 'border-box';

    if (cell.classList.contains('q-align-right')) {
      inner.style.justifyContent = 'flex-end';
      inner.style.textAlign = 'right';
    } else if (cell.classList.contains('q-align-left') || isLabel || isData) {
      inner.style.justifyContent = 'flex-start';
      inner.style.textAlign = 'left';
    } else {
      inner.style.justifyContent = 'center';
      inner.style.textAlign = 'center';
    }

    cell.querySelectorAll('p').forEach((paragraph) => {
      paragraph.style.margin = '0';
      paragraph.style.padding = '0';
      paragraph.style.lineHeight = '1.35';
      paragraph.style.color = PDF_BLACK;
    });
  });
};

const preparePageForCapture = async (pageElement, scrollContainer) => {
  if (!pageElement) {
    return () => {};
  }

  if (scrollContainer) {
    const containerRect = scrollContainer.getBoundingClientRect();
    const pageRect = pageElement.getBoundingClientRect();
    scrollContainer.scrollTop += pageRect.top - containerRect.top - 24;
  } else {
    pageElement.scrollIntoView({ block: 'start' });
  }

  await new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
  await new Promise((resolve) => setTimeout(resolve, 250));

  const images = Array.from(pageElement.querySelectorAll('img'));
  const originalSources = images.map((img) => img.src);

  await Promise.all(
    images.map(async (img) => {
      await waitForImageElement(img);
      const dataUrl = await convertImageSourceToDataUrl(img.src);
      if (dataUrl && dataUrl !== img.src) {
        img.src = dataUrl;
        await waitForImageElement(img);
      }
    })
  );

  return () => {
    images.forEach((img, index) => {
      if (originalSources[index]) {
        img.src = originalSources[index];
      }
    });
  };
};

const formatReadableDate = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// A4 dimensions in pixels (at 96 DPI)
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MM_TO_PX = 3.779527559; // 1mm = 3.779527559px at 96 DPI
const A4_WIDTH_PX = A4_WIDTH_MM * MM_TO_PX; // ~794px
const A4_HEIGHT_PX = A4_HEIGHT_MM * MM_TO_PX; // ~1123px
const PAGE_MARGIN = 5; // 5mm margin
const CONTENT_HEIGHT_PX = A4_HEIGHT_PX - (PAGE_MARGIN * 2 * MM_TO_PX);

const resolveTaxBaseLabel = (applyOn) => {
  switch (applyOn) {
    case 'subtotal':
      return 'subtotal';
    case 'subtotalAfterDiscount':
      return 'subtotal after discount';
    case 'shippingHandling':
      return 'shipping / handling';
    case 'insurance':
      return 'insurance';
    case 'netTotalBeforeTax':
      return 'net total (before tax)';
    default:
      return 'subtotal';
  }
};

const sectionBaseStyle = {
  pageBreakInside: 'avoid',
  breakInside: 'avoid',
};

const escapeHtml = (value) =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const toSanitizedHtml = (value) => {
  if (value == null) return '';
  const raw = String(value).trim();
  if (!raw) return '';

  // If it looks like HTML (rich text editor output), trust structure but sanitize.
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
  const html = looksLikeHtml ? raw : escapeHtml(raw).replace(/\r?\n/g, '<br/>');

  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
  });
};

const Section = ({
  children,
  forcePageBreak = false,
  className = '',
  style: customStyle = {},
}) => {
  const style = forcePageBreak
    ? {
        ...sectionBaseStyle,
        pageBreakAfter: 'always',
        breakAfter: 'page',
        ...customStyle,
      }
    : { ...sectionBaseStyle, ...customStyle };

  return (
    <section className={className} style={style}>
      {children}
      {forcePageBreak && (
        <div className="mt-6 text-center text-[10px] uppercase tracking-[0.3em] text-gray-400 print:hidden">
          — Page Break —
        </div>
      )}
    </section>
  );
};

const EstimationQuotationPrint = ({ quotation, isOpen, onClose, formatCurrency }) => {
  const scrollRef = useRef(null);
  const printableRefs = useRef([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [estimatedPages, setEstimatedPages] = useState(1);
  const [pageContent, setPageContent] = useState([]);
  const [siteLogoUrl, setSiteLogoUrl] = useState(null);
  const [resolvedBankDetails, setResolvedBankDetails] = useState({});
  const siteLogoBlobRef = useRef(null);
  const logoLoadPromiseRef = useRef(Promise.resolve());
  const formatPdfMoney = (amount = 0) => {
    const value = Number.isFinite(Number(amount)) ? Number(amount) : 0;
    return `Rs.${value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };
  const displayMoney = formatPdfMoney;

  const lineItems = quotation?.lineItems || [];
  const charges = quotation?.charges || {};
  const summary = quotation?.summary || {};
  const hsnSacTaxRows = quotation?.hsnSacTaxSummary?.rows || [];
  const taxTotals = quotation?.hsnSacTaxSummary?.totals || {};
  const taxes =
    hsnSacTaxRows.length > 0
      ? hsnSacTaxRows.map((row) => ({
          hsn: row.code,
          sacCode: row.code,
          taxableValue: row.taxableValue,
          centralRate: row.centralRate,
          centralAmount: row.centralAmount,
          stateRate: row.stateRate,
          stateAmount: row.stateAmount,
          igstRate: row.igstRate,
          igstAmount: row.igstAmount,
          amount: row.totalTax,
        }))
      : summary.taxes || quotation?.taxRows || [];

  const asNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatTaxRate = (rate) => {
    const value = asNumber(rate);
    return value > 0 ? `${value}%` : '—';
  };

  const brand = quotation?.brand || {};
  const bankDetails =
    resolvedBankDetails && Object.values(resolvedBankDetails).some(Boolean)
      ? resolvedBankDetails
      : quotation?.bankDetails && Object.values(quotation.bankDetails).some(Boolean)
        ? quotation.bankDetails
        : buildBankDetails({
            tenantInfo: quotation?.tenantInfo,
            site: quotation?.selectedSite,
            apiQuotation: quotation?.apiQuotation,
          });
  const displayLogoUrl = siteLogoUrl || brand.logoUrl || null;

  useEffect(() => {
    if (!isOpen || !quotation) {
      setResolvedBankDetails({});
      return undefined;
    }

    const preset =
      quotation.bankDetails && Object.values(quotation.bankDetails).some(Boolean)
        ? quotation.bankDetails
        : buildBankDetails({
            tenantInfo: quotation.tenantInfo,
            site: quotation.selectedSite,
            apiQuotation: quotation.apiQuotation,
          });

    if (Object.values(preset).some(Boolean)) {
      setResolvedBankDetails(preset);
      return undefined;
    }

    let cancelled = false;

    fetchBankDetailsForQuotation({
      siteId:
        quotation.siteId ||
        quotation.formData?.siteSelection ||
        quotation.apiQuotation?.site_id,
      site: quotation.selectedSite,
      tenantInfo: quotation.tenantInfo,
      apiQuotation: quotation.apiQuotation,
    })
      .then((details) => {
        if (!cancelled) {
          setResolvedBankDetails(details);
        }
      })
      .catch((error) => {
        console.warn('[EstimationQuotationPrint] Unable to resolve bank details:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, quotation]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !quotation) {
      setSiteLogoUrl(null);
      return undefined;
    }

    let isMounted = true;

    const resolveLogo = async () => {
      if (brand.logoUrl) {
        const brandedLogo = await convertImageSourceToDataUrl(brand.logoUrl);
        if (isMounted) {
          setSiteLogoUrl(brandedLogo || brand.logoUrl);
        }
        return;
      }

      const siteRecord =
        quotation.selectedSite ||
        (quotation.siteId || quotation.formData?.siteSelection || quotation.apiQuotation?.site_id
          ? {
              site_id:
                quotation.siteId ||
                quotation.formData?.siteSelection ||
                quotation.apiQuotation?.site_id,
            }
          : null);

      if (!siteRecord) {
        if (isMounted) {
          setSiteLogoUrl(null);
        }
        return;
      }

      const resolvedUrl = await resolveSiteLogoUrl(siteRecord);
      if (!isMounted) {
        if (resolvedUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(resolvedUrl);
        }
        return;
      }

      if (siteLogoBlobRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(siteLogoBlobRef.current);
      }

      const displayUrl = resolvedUrl
        ? await convertImageSourceToDataUrl(resolvedUrl)
        : null;

      if (resolvedUrl?.startsWith('blob:') && displayUrl !== resolvedUrl) {
        URL.revokeObjectURL(resolvedUrl);
        siteLogoBlobRef.current = null;
      } else if (resolvedUrl?.startsWith('blob:')) {
        siteLogoBlobRef.current = resolvedUrl;
      } else {
        siteLogoBlobRef.current = null;
      }

      setSiteLogoUrl(displayUrl || resolvedUrl || null);
    };

    logoLoadPromiseRef.current = resolveLogo();

    return () => {
      isMounted = false;
      if (siteLogoBlobRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(siteLogoBlobRef.current);
        siteLogoBlobRef.current = null;
      }
    };
  }, [isOpen, quotation, brand.logoUrl]);

  // Initialize pageContent when component opens
  useEffect(() => {
    if (!isOpen) {
      setPageContent([]);
      printableRefs.current = [];
    }
  }, [isOpen]);

  const meta = useMemo(() => {
    if (!quotation) {
      return {};
    }

    const { formData = {}, labels = {} } = quotation;
    const readableDate = formatReadableDate(formData.date);
    const readableExpiry = formatReadableDate(formData.expiryDate);
    const generatedOn = formatReadableDate(quotation.generatedAt) || formatReadableDate(new Date());

    return {
      quoteNumber: quotation.quotationNumber || 'Draft Quotation',
      generatedOn,
      date: readableDate,
      expiryDate: readableExpiry,
      paymentMode: formData.paymentMode || '—',
      paymentTerms: formData.paymentTerms || '—',
      site: labels.site || formData.siteSelection || '—',
      client: labels.client || formData.clientSelectionShipping || '—',
      shippingPackages: formData.shippingPackages || '—',
      grossQuantity: formData.grossQuantity || '—',
      netQuantity: formData.netQuantity || '—',
      specialNotes: formData.specialNotes || '—',
      termsOfSale: formData.termsOfSale || '—',
      vendorPoc: {
        name: formData.pocVendorName || '—',
        role: formData.pocVendorRole || '—',
        phone: formData.pocVendorPhone || '—',
        email: formData.pocVendorEmail || '—',
        gstin:
          resolvePocGstin(quotation?.apiQuotation?.poc_vendor) ||
          formData.pocVendorGstin ||
          '—',
      },
      clientPoc: {
        name: formData.pocClientName || '—',
        role: formData.pocClientRole || '—',
        phone: formData.pocClientPhone || '—',
        email: formData.pocClientEmail || '—',
        gstin:
          resolvePocGstin(quotation?.apiQuotation?.poc_client) ||
          formData.pocClientGstin ||
          '—',
      },
      vendorSign: formData.vendorSign || quotation.vendorSign || '',
      clientSign: formData.clientSign || quotation.clientSign || '',
    };
  }, [quotation]);

  const termsHtml = useMemo(() => {
    if (Array.isArray(quotation?.licenseClauses) && quotation.licenseClauses.length) {
      const items = quotation.licenseClauses
        .map((item) => String(item ?? '').trim())
        .filter(Boolean);
      if (!items.length) return '';
      const html = `<ol>${items.map((t) => `<li>${escapeHtml(t)}</li>`).join('')}</ol>`;
      return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    }

    return toSanitizedHtml(meta?.termsOfSale);
  }, [meta?.termsOfSale, quotation?.licenseClauses]);
  const vendorDisplayName =
    resolveVendorDisplayName(quotation?.formData) ||
    quotation?.vendor?.name ||
    (meta.vendorPoc?.name && meta.vendorPoc.name !== '—' ? meta.vendorPoc.name : '');

  const resolvedVendor = {
    ...(quotation?.vendor ||
      buildVendorFromSite(quotation?.selectedSite, {
        formData: quotation?.formData,
        siteLabel: quotation?.siteName || quotation?.labels?.site || meta.site,
      }) ||
      {}),
    name: vendorDisplayName,
  };
  const resolvedCustomer =
    quotation?.customer ||
    buildCustomerFromRecord(quotation?.selectedCustomer, {
      formData: quotation?.formData,
      fallbackName: meta.client,
    }) ||
    {};
  const vendorInfo = resolvedVendor;
  const customerInfo = resolvedCustomer;
  const poweredByText = brand.poweredBy || 'Powered by innoworx.ai';

  const vendorPhoneEmail =
    formatPhoneEmail(meta.vendorPoc?.phone, meta.vendorPoc?.email) ||
    formatPhoneEmail(quotation?.formData?.pocVendorPhone, quotation?.formData?.pocVendorEmail) ||
    vendorInfo.phoneEmail ||
    formatPhoneEmail(vendorInfo.phone, vendorInfo.email);
  const customerPhoneEmail =
    customerInfo.phoneEmail ||
    formatPhoneEmail(customerInfo.phone, customerInfo.email) ||
    formatPhoneEmail(meta.clientPoc?.phone, meta.clientPoc?.email);

  const vendorRows = [
    {
      label: 'Vendor Name',
      value: vendorDisplayName || '—',
    },
    {
      label: 'Shipping Address',
      value:
        vendorInfo.shippingAddress ||
        vendorInfo.address ||
        quotation?.selectedSite?.address ||
        quotation?.formData?.vendorAddress ||
        '—',
    },
    {
      label: 'Phone , Email',
      value: vendorPhoneEmail || '—',
    },
    {
      label: 'GSTIN',
      value:
        resolvePocGstin(quotation?.apiQuotation?.poc_vendor) ||
        quotation?.formData?.pocVendorGstin ||
        (meta.vendorPoc?.gstin && meta.vendorPoc.gstin !== '—'
          ? meta.vendorPoc.gstin
          : '') ||
        vendorInfo.gstin ||
        brand.gstin ||
        '—',
    },
  ];

  const customerRows = [
    {
      label: 'Customer Name',
      value: customerInfo.name || meta.client || quotation?.formData?.customerName || '—',
    },
    {
      label: 'Billing Address',
      value:
        customerInfo.billingAddress ||
        customerInfo.address ||
        quotation?.formData?.clientAddress ||
        '—',
    },
    {
      label: 'Phone , Email',
      value: customerPhoneEmail || '—',
    },
    {
      label: 'GSTIN',
      value:
        resolvePocGstin(quotation?.apiQuotation?.poc_client) ||
        quotation?.formData?.pocClientGstin ||
        customerInfo.gstin ||
        '—',
    },
  ];

  const combinedPartyRows = Array.from(
    { length: Math.max(vendorRows.length, customerRows.length) },
    (_, idx) => ({
      vendor: vendorRows[idx],
      customer: customerRows[idx],
    })
  );

  const totalAmountWords =
    quotation?.amountInWords ||
    summary?.amountInWords ||
    quotation?.formData?.amountInWords ||
    '';

  const gstFooterLabel = useMemo(() => {
    const subtotal = summary.subtotal || 0;
    const totalTax = summary.totalTax || 0;
    if (subtotal > 0 && totalTax > 0) {
      const pct = ((totalTax / subtotal) * 100).toFixed(2);
      return `GST(${pct}%)`;
    }
    return 'GST';
  }, [summary.subtotal, summary.totalTax]);

  const formatTotalQuantity = (qty) =>
    Number.isInteger(qty) ? String(qty) : Number(qty || 0).toFixed(2);

  // Calculate page content distribution
  useEffect(() => {
    if (isOpen && quotation && lineItems.length >= 0) {
      // Estimate section heights (in pixels) — tuned for compact PDF typography + 20px section gaps
      const headerHeight = 132;
      const vendorCustomerHeight = 84;
      const lineItemTableHeaderHeight = 26;
      const lineItemRowHeight = 28;
      const totalsHeight = 72;
      const taxesHeight = 145;
      const bankDetailsHeight = 88;
      const termsHeight = 110;
      const representativesHeight = 108;
      const footerHeight = 24;
      
      const pages = [];
      let currentPageNum = 1;
      let currentPageHeight = 0;
      let currentPageSections = [];
      let currentPageLineItems = [];
      
      // Helper function to add a new page
      const addNewPage = (isFirstPage = false) => {
        if (currentPageNum > 1 || currentPageSections.length > 0) {
          pages.push({
            pageNumber: currentPageNum,
            lineItems: [...currentPageLineItems],
            showHeader: true, // Always show header on all pages
            showVendorCustomer: currentPageSections.includes('vendorCustomer'),
            showTotals: currentPageSections.includes('totals'),
            showTaxes: currentPageSections.includes('taxes'),
            showBankDetails: currentPageSections.includes('bankDetails'),
            showTerms: currentPageSections.includes('terms'),
            showRepresentatives: currentPageSections.includes('representatives'),
            isContinuation: !isFirstPage,
          });
          currentPageNum++;
        }
        currentPageHeight = headerHeight; // Always include header height
        currentPageSections = ['header']; // Always include header
        currentPageLineItems = [];
      };
      
      // Start with first page
      addNewPage(true);
      
      // Section 1: Vendor/Customer Info
      if (currentPageHeight + vendorCustomerHeight <= CONTENT_HEIGHT_PX) {
        currentPageHeight += vendorCustomerHeight;
        currentPageSections.push('vendorCustomer');
      } else {
        addNewPage();
        currentPageHeight += vendorCustomerHeight;
        currentPageSections.push('vendorCustomer');
      }
      
      // Section 2: Line Items Table (can be split across pages)
      const totalLineItems = lineItems.length;
      let lineItemIndex = 0;
      let allItemsAdded = false;
      
      while (lineItemIndex < totalLineItems) {
        const remainingHeight = CONTENT_HEIGHT_PX - currentPageHeight;
        const availableForItems = remainingHeight - lineItemTableHeaderHeight;
        const itemsThatFit = Math.max(0, Math.floor(availableForItems / lineItemRowHeight));
        
        if (itemsThatFit === 0 || currentPageHeight + lineItemTableHeaderHeight > CONTENT_HEIGHT_PX) {
          // Can't fit even one item, start new page
          addNewPage();
          continue;
        }
        
        const itemsToAdd = Math.min(itemsThatFit, totalLineItems - lineItemIndex);
        const itemsForThisPage = lineItems.slice(lineItemIndex, lineItemIndex + itemsToAdd);
        
        // Add items to current page
        currentPageLineItems = [...currentPageLineItems, ...itemsForThisPage];
        currentPageHeight += lineItemTableHeaderHeight + (itemsToAdd * lineItemRowHeight);
        currentPageSections.push('lineItems');
        lineItemIndex += itemsToAdd;
        
        // Check if all items are added
        if (lineItemIndex >= totalLineItems) {
          allItemsAdded = true;
          // Check if we can add totals on this page
          if (currentPageHeight + totalsHeight <= CONTENT_HEIGHT_PX) {
            currentPageHeight += totalsHeight;
            currentPageSections.push('totals');
          } else {
            // Totals will be added to next page
            addNewPage();
            if (currentPageHeight + totalsHeight <= CONTENT_HEIGHT_PX) {
              currentPageHeight += totalsHeight;
              currentPageSections.push('totals');
            }
          }
          break;
        }
        
        // If we've added items but there are more, start a new page for the next batch
        if (lineItemIndex < totalLineItems) {
          addNewPage();
        }
      }
      
      // If no line items, still add totals section
      if (totalLineItems === 0) {
        if (currentPageHeight + totalsHeight <= CONTENT_HEIGHT_PX) {
          currentPageHeight += totalsHeight;
          currentPageSections.push('totals');
        } else {
          addNewPage();
          currentPageHeight += totalsHeight;
          currentPageSections.push('totals');
        }
      }
      
      // Section 3: Tax Table
      if (currentPageHeight + taxesHeight <= CONTENT_HEIGHT_PX) {
        currentPageHeight += taxesHeight;
        currentPageSections.push('taxes');
      } else {
        addNewPage();
        currentPageHeight += taxesHeight;
        currentPageSections.push('taxes');
      }
      
      // Section 4: Bank Details
      if (currentPageHeight + bankDetailsHeight <= CONTENT_HEIGHT_PX) {
        currentPageHeight += bankDetailsHeight;
        currentPageSections.push('bankDetails');
      } else {
        addNewPage();
        currentPageHeight += bankDetailsHeight;
        currentPageSections.push('bankDetails');
      }
      
      // Section 5: Terms & Conditions
      if (currentPageHeight + termsHeight <= CONTENT_HEIGHT_PX) {
        currentPageHeight += termsHeight;
        currentPageSections.push('terms');
      } else {
        addNewPage();
        currentPageHeight += termsHeight;
        currentPageSections.push('terms');
      }
      
      // Section 6: Representatives
      if (currentPageHeight + representativesHeight <= CONTENT_HEIGHT_PX) {
        currentPageHeight += representativesHeight;
        currentPageSections.push('representatives');
      } else {
        addNewPage();
        currentPageHeight += representativesHeight;
        currentPageSections.push('representatives');
      }
      
      // Add footer to last page
      if (currentPageHeight + footerHeight <= CONTENT_HEIGHT_PX) {
        currentPageSections.push('footer');
      } else {
        addNewPage();
        currentPageSections.push('footer');
      }
      
      // Add the last page
      if (currentPageSections.length > 0) {
        pages.push({
          pageNumber: currentPageNum,
          lineItems: [...currentPageLineItems],
          showHeader: currentPageSections.includes('header'),
          showVendorCustomer: currentPageSections.includes('vendorCustomer'),
          showTotals: currentPageSections.includes('totals'),
          showTaxes: currentPageSections.includes('taxes'),
          showBankDetails: currentPageSections.includes('bankDetails'),
          showTerms: currentPageSections.includes('terms'),
          showRepresentatives: currentPageSections.includes('representatives'),
          isContinuation: !currentPageSections.includes('header'),
        });
      }
      
      setEstimatedPages(pages.length);
      setPageContent(pages);
    }
  }, [isOpen, quotation, lineItems.length]);

  if (!isOpen || !quotation) {
    return null;
  }

  const handleDownloadPdf = async () => {
    if (!printableRefs.current || printableRefs.current.length === 0 || isGeneratingPdf) {
      return;
    }
    try {
      setIsGeneratingPdf(true);
      await logoLoadPromiseRef.current;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 5;
      const availableWidth = pageWidth - margin * 2;
      const availableHeight = pageHeight - margin * 2;

      // Process each page
      for (let i = 0; i < printableRefs.current.length; i++) {
        const pageRef = printableRefs.current[i];
        if (!pageRef) continue;

        const restorePage = await preparePageForCapture(pageRef, scrollRef.current);

        try {
          if (i > 0) {
            pdf.addPage();
          }

          const canvas = await html2canvas(pageRef, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            width: pageRef.scrollWidth,
            height: pageRef.scrollHeight,
            windowWidth: pageRef.scrollWidth,
            windowHeight: pageRef.scrollHeight,
            scrollX: 0,
            scrollY: 0,
            onclone: (clonedDoc) => {
              injectQuotationPdfStyles(clonedDoc);
              clonedDoc.querySelectorAll('.pdf-page').forEach((page) => {
                preparePdfPageForCapture(page);
              });
            },
          });

          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          const widthRatio = availableWidth / imgWidth;
          const heightRatio = availableHeight / imgHeight;
          const ratio = Math.min(widthRatio, heightRatio);
          const scaledWidth = imgWidth * ratio;
          const scaledHeight = imgHeight * ratio;

          const offsetX = (pageWidth - scaledWidth) / 2;
          const offsetY = (pageHeight - scaledHeight) / 2;

          pdf.addImage(
            canvas.toDataURL('image/png', 1.0),
            'PNG',
            offsetX,
            offsetY,
            scaledWidth,
            scaledHeight,
            undefined,
            'FAST'
          );
        } finally {
          restorePage();
        }
      }

      pdf.save(`${quotation.quotationNumber || 'quotation'}.pdf`);
    } catch (error) {
      console.error('Failed to generate quotation PDF', error);
      alert('Unable to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-800">
      {/* Print Styles */}
      <style>{`
        /* These styles must apply in "screen" too because html2canvas captures screen styles. */
        .pdf-page {
          color: #000000;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 9px;
          line-height: 1.25;
        }

        .pdf-page .q-section-title {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .pdf-page .q-note {
          font-size: 8px;
          line-height: 1.3;
        }

        .pdf-page .q-amount-words {
          font-size: 8px;
          line-height: 1.35;
          color: #000000;
          font-weight: 700;
          margin: 0;
        }

        /* Reference palette — match formal quotation spec */
        .pdf-page {
          --q-border: #000000;
          --q-peach: #F9D9C9;
          box-sizing: border-box;
        }

        /* Single frame border — same weight on all four sides */
        .pdf-page .pdf-page-inner {
          border: 1px solid #000000;
          box-sizing: border-box;
          min-height: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          padding: 8px;
        }

        .pdf-page .q-box {
          border: 1px solid #000000;
          border-radius: 0;
        }

        .pdf-page .q-box.q-table-frame {
          border: 0;
          padding: 0;
        }

        .pdf-page .section-vendor-customer {
          overflow: visible !important;
        }

        .pdf-page .pdf-page-content {
          display: flex;
          flex-direction: column;
          gap: 12px;
          flex: 1 1 auto;
        }

        .pdf-page .pdf-page-bottom {
          flex-shrink: 0;
          margin-top: auto;
          width: 100%;
        }

        .pdf-page .q-powered-by-block {
          text-align: center;
          padding-top: 8px;
        }

        .pdf-page .q-powered-by-text {
          margin: 0;
          font-size: 7px;
          font-weight: 400;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #000000;
        }

        .pdf-page .q-powered-by-line {
          display: block;
          width: 100%;
          height: 1px;
          min-height: 1px;
          margin: 8px 0 6px;
          padding: 0;
          border: 0;
          background-color: #000000;
        }

        .pdf-page .pdf-page-footer {
          margin-top: 0;
          padding-top: 4px;
          border-top: 0;
          text-align: center;
          font-size: 8px;
          color: #000000;
        }

        .pdf-page .pdf-page-header {
          padding-bottom: 10px;
          border-bottom: 1px solid #000000;
        }

        .pdf-page .pdf-page-header .q-title {
          font-size: 18px;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: #111827;
        }

        .pdf-page .pdf-page-header .q-meta {
          font-size: 8px;
          color: #374151;
        }

        .pdf-page table.q-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }

        .pdf-page table.q-table th,
        .pdf-page table.q-table td {
          border: 1px solid #000000;
          vertical-align: middle;
          padding: 6px;
          font-size: 8px;
          line-height: 1.35;
          word-wrap: break-word;
          overflow-wrap: anywhere;
          box-sizing: border-box;
          background: #ffffff;
          color: #000000;
        }

        .pdf-page table.q-table .q-align-left,
        .pdf-page table.q-table th.q-align-left,
        .pdf-page table.q-table td.q-align-left {
          text-align: left;
        }

        .pdf-page table.q-table .q-align-right,
        .pdf-page table.q-table th.q-align-right,
        .pdf-page table.q-table td.q-align-right {
          text-align: right;
        }

        .pdf-page table.q-table .q-align-center,
        .pdf-page table.q-table th.q-align-center,
        .pdf-page table.q-table td.q-align-center {
          text-align: center;
        }

        .pdf-page table.q-table .q-cell-inner {
          display: flex;
          align-items: center;
          min-height: 14px;
          width: 100%;
          box-sizing: border-box;
          line-height: 1.35;
        }

        .pdf-page table.q-table .q-align-left .q-cell-inner,
        .pdf-page table.q-table td.q-label-cell .q-cell-inner {
          justify-content: flex-start;
          text-align: left;
        }

        .pdf-page table.q-table .q-align-right .q-cell-inner {
          justify-content: flex-end;
          text-align: right;
        }

        .pdf-page table.q-table .q-align-center .q-cell-inner {
          justify-content: center;
          text-align: center;
        }

        .pdf-page table.q-table th p,
        .pdf-page table.q-table td p {
          margin: 0;
          padding: 0;
          line-height: 1.35;
        }

        .pdf-page table.q-table thead th {
          background: #F9D9C9;
          font-size: 8px;
          font-weight: 700;
          letter-spacing: 0;
          text-transform: none;
          vertical-align: middle;
        }

        .pdf-page table.q-table .q-th-wrap {
          white-space: normal;
        }

        .pdf-page table.q-table .q-num {
          font-variant-numeric: tabular-nums;
        }

        .pdf-page table.q-table col.q-col-no { width: 5%; }
        .pdf-page table.q-table col.q-col-desc { width: 34%; }
        .pdf-page table.q-table col.q-col-sac { width: 13%; }
        .pdf-page table.q-table col.q-col-qty { width: 10%; }
        .pdf-page table.q-table col.q-col-rate { width: 19%; }
        .pdf-page table.q-table col.q-col-total { width: 19%; }

        .pdf-page table.q-table col.q-col-hsn { width: 8%; }
        .pdf-page table.q-table col.q-col-taxable { width: 11%; }
        .pdf-page table.q-table col.q-col-crate { width: 7%; }
        .pdf-page table.q-table col.q-col-camt { width: 9%; }
        .pdf-page table.q-table col.q-col-srate { width: 7%; }
        .pdf-page table.q-table col.q-col-samt { width: 9%; }
        .pdf-page table.q-table col.q-col-irate { width: 7%; }
        .pdf-page table.q-table col.q-col-iamt { width: 9%; }
        .pdf-page table.q-table col.q-col-tamt { width: 12%; }

        .pdf-page table.q-table.section-tax-table th,
        .pdf-page table.q-table.section-tax-table td {
          font-size: 7px;
          padding: 5px 3px;
        }

        .pdf-page table.q-table td.q-label-cell {
          background: #F9D9C9;
          font-weight: 700;
          text-transform: none;
          vertical-align: middle;
          text-align: left;
        }

        .pdf-page table.q-table td.q-data-cell {
          background: #ffffff;
          font-weight: 400;
          text-align: left;
        }

        .pdf-page table.q-table tr.q-summary-label td {
          background: #F9D9C9;
          font-weight: 700;
        }

        .pdf-page table.q-table tr.q-total-row td {
          background: #F9D9C9;
          font-weight: 700;
          font-size: 8px;
        }

        .pdf-page table.q-table tfoot tr.q-tax-total td {
          background: #ffffff;
          font-weight: 700;
        }

        .pdf-page .q-box.q-panel-body,
        .pdf-page .q-box .q-panel-body {
          padding: 10px 12px 18px;
          box-sizing: border-box;
          overflow: visible;
        }

        .pdf-page .q-panel-lines {
          display: flex;
          flex-direction: column;
          gap: 5px;
          margin-top: 6px;
          padding-bottom: 4px;
        }

        .pdf-page .q-panel-lines p,
        .pdf-page .q-panel-lines .q-signature-row {
          margin: 0;
          line-height: 1.5;
        }

        .pdf-page .q-panel-lines > *:last-child {
          padding-bottom: 2px;
        }

        .pdf-page .q-signature-row {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          line-height: 1.5;
          min-height: 14px;
        }

        .pdf-page .q-signature-row svg {
          width: 10px;
          height: 10px;
          flex-shrink: 0;
          display: block;
        }

        .pdf-page .q-box.q-panel-body .ql-editor {
          margin-top: 6px;
          padding-bottom: 4px !important;
        }

        .pdf-page .q-box .ql-editor,
        .pdf-page .q-box .ql-editor * {
          font-size: 8px !important;
          line-height: 1.3 !important;
          color: #374151;
        }

        .pdf-page .q-box .ql-editor ol,
        .pdf-page .q-box .ql-editor ul {
          padding-left: 14px;
          margin: 0;
        }

        /* Tables inside table frames: full grid comes from cell borders only */
        .pdf-page .q-box.q-table-frame table.q-table {
          border: 0;
        }

        @media print {
          .pdf-viewer-toolbar,
          .pdf-viewer-controls {
            display: none !important;
          }
          .pdf-page {
            page-break-after: always;
            margin: 0 !important;
            box-shadow: none !important;
            break-after: page;
          }
          .pdf-viewer-container {
            background: white !important;
            padding: 0 !important;
          }
        }
      `}</style>

      {/* PDF Viewer Toolbar */}
      <div className="pdf-viewer-toolbar flex-shrink-0 bg-gray-900 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-base font-semibold" style={{ color: '#fff' }}>
              Estimation · Quotation Preview - PDF Viewer
            </h2>
            <div className="h-5 w-px bg-gray-600"></div>
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <span className="font-medium">Estimated Pages:</span>
              <span className="px-2 py-1 bg-gray-700 rounded text-white font-semibold">
                {estimatedPages} {estimatedPages === 1 ? 'page' : 'pages'}
              </span>
            </div>
            <div className="h-5 w-px bg-gray-600"></div>
            <div className="text-xs text-gray-400">
              A4 Size (210mm × 297mm)
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={isGeneratingPdf}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isGeneratingPdf ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Generating...
                </>
              ) : (
                <>
                  <span>📥</span>
                  Download PDF
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* PDF Viewer Container */}
      <div className="pdf-viewer-container flex-1 overflow-auto bg-gray-600 p-8" ref={scrollRef}>
        <div className="flex flex-col items-center gap-6 min-h-full py-8">
          {pageContent.length > 0 ? (
            pageContent.map((page, pageIndex) => {
              const isLastPage = pageIndex === pageContent.length - 1;
              // Calculate starting line number for continuation pages
              const startLineNumber = pageIndex === 0 
                ? 1 
                : pageContent.slice(0, pageIndex).reduce((sum, p) => sum + p.lineItems.length, 0) + 1;

              return (
                <div
                  key={page.pageNumber}
                  ref={(el) => {
                    if (el) printableRefs.current[pageIndex] = el;
                  }}
                  className="pdf-page bg-white shadow-2xl"
                  style={{
                    width: `${A4_WIDTH_PX}px`,
                    minHeight: `${A4_HEIGHT_PX}px`,
                    padding: `${PAGE_MARGIN * MM_TO_PX}px`,
                    boxSizing: 'border-box',
                  }}
                >
                  <style dangerouslySetInnerHTML={{ __html: QUOTATION_PDF_EMBEDDED_STYLES }} />
                  <div className="pdf-page-inner">
                  <div className="pdf-page-content">
                    {/* Header - displayed on all pages */}
                    <Section className="pdf-page-header flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div className="flex items-center gap-2">
                        {displayLogoUrl ? (
                          <img
                            src={displayLogoUrl}
                            alt={brand.companyName || 'Quotation Logo'}
                            className="h-12 w-20 object-contain"
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <div className="flex h-12 w-20 items-center justify-center border border-dashed border-gray-300 text-[8px] font-semibold text-gray-500">
                            Logo
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="q-title">QUOTATION</p>
                        <p className="q-meta mt-0.5 font-semibold tracking-[0.12em]">
                          {meta.quoteNumber}
                        </p>
                        <div className="q-meta mt-1 space-y-0 font-medium">
                          <p>
                            Issued Date:{' '}
                            <span className="font-semibold text-gray-900">{meta.date || '—'}</span>
                          </p>
                          <p>
                            Valid Until:{' '}
                            <span className="font-semibold text-gray-900">
                              {meta.expiryDate || '—'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </Section>

                     {/* Section 1: Vendor/Customer Info */}
                     {page.showVendorCustomer && (
                       <div className="section-vendor-customer q-box q-table-frame overflow-hidden">
                         <table className="q-table">
                           <colgroup>
                             <col style={{ width: '14%' }} />
                             <col style={{ width: '36%' }} />
                             <col style={{ width: '14%' }} />
                             <col style={{ width: '36%' }} />
                           </colgroup>
                           <tbody>
                             {combinedPartyRows.map((row, idx) => (
                               <tr key={`party-row-${idx}`}>
                                 <td className="q-label-cell">{row.vendor?.label || ''}</td>
                                 <td className="q-data-cell">{row.vendor?.value || '—'}</td>
                                 <td className="q-label-cell">{row.customer?.label || ''}</td>
                                 <td className="q-data-cell">{row.customer?.value || '—'}</td>
                               </tr>
                             ))}
                           </tbody>
                         </table>
                       </div>
                     )}

                     {/* Section 2: Line Items Table */}
                     {page.lineItems.length > 0 && (
                       <div className="section-line-items q-box q-table-frame overflow-hidden">
                         <table className="q-table">
                           <colgroup>
                             <col className="q-col-no" />
                             <col className="q-col-desc" />
                             <col className="q-col-sac" />
                             <col className="q-col-qty" />
                             <col className="q-col-rate" />
                             <col className="q-col-total" />
                           </colgroup>
                           <thead>
                             <tr>
                               <th className="q-align-center">No.</th>
                               <th className="q-align-left">Description</th>
                               <th className="q-align-center q-th-wrap">SAC &amp; HSN CODE</th>
                               <th className="q-align-center">Quantity</th>
                               <th className="q-align-right">Unit Price</th>
                               <th className="q-align-right">Total</th>
                             </tr>
                           </thead>
                           <tbody>
                             {page.lineItems.map((item, itemIndex) => {
                               const lineNumber = startLineNumber + itemIndex;
                               return (
                                 <tr key={`line-item-${page.pageNumber}-${itemIndex}`}>
                                   <td className="q-align-center q-num font-semibold">
                                     {item.lineNumber || lineNumber}
                                   </td>
                                   <td className="q-align-left">
                                     <p>{item.name || `Line item ${lineNumber}`}</p>
                                     {item.description && item.description !== item.name && (
                                       <p className="q-note">{item.description}</p>
                                     )}
                                   </td>
                                   <td className="q-align-center">{item.sacCode || item.hsnSacCode || '—'}</td>
                                   <td className="q-align-center q-num">
                                     {item.quantity || '-'}
                                   </td>
                                   <td className="q-align-right q-num">
                                     {displayMoney(parseFloat(item.rate) || 0)}
                                   </td>
                                   <td className="q-align-right q-num font-semibold">
                                     {displayMoney(parseFloat(item.amount) || 0)}
                                   </td>
                                 </tr>
                               );
                             })}
                           </tbody>
                           {page.showTotals && (
                             <tfoot>
                               <tr className="q-summary-label">
                                 <td colSpan={5} className="q-align-right">
                                   Subtotal (Taxable Amount)
                                 </td>
                                 <td className="q-align-right q-num">{displayMoney(summary.subtotal || 0)}</td>
                               </tr>
                               <tr className="q-summary-label">
                                 <td colSpan={5} className="q-align-right">
                                   {gstFooterLabel}
                                 </td>
                                 <td className="q-align-right q-num">{displayMoney(summary.totalTax || 0)}</td>
                               </tr>
                               <tr className="q-total-row">
                                 <td colSpan={2} className="q-align-left">
                                   Total
                                 </td>
                                 <td></td>
                                 <td className="q-align-center q-num">
                                   {summary.totalQuantity
                                     ? formatTotalQuantity(summary.totalQuantity)
                                     : ''}
                                 </td>
                                 <td></td>
                                 <td className="q-align-right q-num">
                                   {displayMoney(summary.grandTotal || 0)}
                                 </td>
                               </tr>
                             </tfoot>
                           )}
                         </table>
                       </div>
                     )}

                     {page.showTotals && totalAmountWords && page.lineItems.length > 0 && (
                       <p className="q-amount-words">
                         Total amount(in words): {totalAmountWords} E. &amp; O.E
                       </p>
                     )}

                     {/* Totals only (when line items are on previous page) */}
                     {page.showTotals && page.lineItems.length === 0 && (
                       <div className="section-totals q-box q-table-frame overflow-hidden">
                         <table className="q-table">
                           <colgroup>
                             <col className="q-col-no" />
                             <col className="q-col-desc" />
                             <col className="q-col-sac" />
                             <col className="q-col-qty" />
                             <col className="q-col-rate" />
                             <col className="q-col-total" />
                           </colgroup>
                           <tfoot>
                             <tr className="q-summary-label">
                               <td colSpan={5} className="q-align-right">
                                 Subtotal (Taxable Amount)
                               </td>
                               <td className="q-align-right q-num">{displayMoney(summary.subtotal || 0)}</td>
                             </tr>
                             <tr className="q-summary-label">
                               <td colSpan={5} className="q-align-right">
                                 {gstFooterLabel}
                               </td>
                               <td className="q-align-right q-num">{displayMoney(summary.totalTax || 0)}</td>
                             </tr>
                             <tr className="q-total-row">
                               <td colSpan={2} className="q-align-left">
                                 Total
                               </td>
                               <td></td>
                               <td className="q-align-center q-num">
                                 {summary.totalQuantity
                                   ? formatTotalQuantity(summary.totalQuantity)
                                   : ''}
                               </td>
                               <td></td>
                               <td className="q-align-right q-num">
                                 {displayMoney(summary.grandTotal || 0)}
                               </td>
                             </tr>
                           </tfoot>
                         </table>
                       </div>
                     )}

                     {page.showTotals && totalAmountWords && page.lineItems.length === 0 && (
                       <p className="q-amount-words">
                         Total amount(in words): {totalAmountWords} E. &amp; O.E
                       </p>
                     )}

                     {/* Section 3: Tax Table */}
                     {page.showTaxes && (
                       <div className="section-taxes q-box q-table-frame overflow-hidden">
                         <table className="q-table section-tax-table">
                           <colgroup>
                             <col className="q-col-hsn" />
                             <col className="q-col-taxable" />
                             <col className="q-col-crate" />
                             <col className="q-col-camt" />
                             <col className="q-col-srate" />
                             <col className="q-col-samt" />
                             <col className="q-col-irate" />
                             <col className="q-col-iamt" />
                             <col className="q-col-tamt" />
                           </colgroup>
                           <thead>
                             <tr>
                               <th className="q-align-center">HSN/SAC</th>
                               <th className="q-align-right q-th-wrap">Taxable Value</th>
                               <th className="q-align-right q-th-wrap">Central Tax Rate</th>
                               <th className="q-align-right q-th-wrap">Central Tax Amount</th>
                               <th className="q-align-right q-th-wrap">State/UT Tax Rate</th>
                               <th className="q-align-right q-th-wrap">State/UT Tax Amount</th>
                               <th className="q-align-right q-th-wrap">Integrated Tax Rate</th>
                               <th className="q-align-right q-th-wrap">Integrated Tax Amount</th>
                               <th className="q-align-right q-th-wrap">Total Tax Amount</th>
                             </tr>
                           </thead>
                           <tbody>
                             {taxes.length ? (
                               taxes.map((tax, index) => (
                                 <tr key={`tax-row-${index}`}>
                                   <td className="q-align-center">{tax.hsn || tax.sacCode || tax.label || '—'}</td>
                                   <td className="q-align-right q-num">
                                     {tax.taxableValue != null
                                       ? displayMoney(asNumber(tax.taxableValue))
                                       : displayMoney(summary.subtotal || 0)}
                                   </td>
                                   <td className="q-align-right q-num">
                                     {formatTaxRate(tax.centralRate ?? tax.percentage)}
                                   </td>
                                   <td className="q-align-right q-num">
                                     {displayMoney(asNumber(tax.centralAmount))}
                                   </td>
                                   <td className="q-align-right q-num">
                                     {formatTaxRate(tax.stateRate ?? tax.percentage)}
                                   </td>
                                   <td className="q-align-right q-num">
                                     {displayMoney(asNumber(tax.stateAmount))}
                                   </td>
                                   <td className="q-align-right q-num">
                                     {formatTaxRate(tax.igstRate)}
                                   </td>
                                   <td className="q-align-right q-num">
                                     {displayMoney(asNumber(tax.igstAmount))}
                                   </td>
                                   <td className="q-align-right q-num font-semibold">
                                     {displayMoney(
                                       asNumber(tax.amount ?? tax.totalTax ?? 0) ||
                                         asNumber(tax.centralAmount) +
                                           asNumber(tax.stateAmount) +
                                           asNumber(tax.igstAmount)
                                     )}
                                   </td>
                                 </tr>
                               ))
                             ) : (
                               <tr>
                                 <td colSpan={9} className="q-align-center">
                                   Tax details will appear here once configured.
                                 </td>
                               </tr>
                             )}
                           </tbody>
                           {taxes.length > 0 && (
                             <tfoot>
                               <tr className="q-tax-total">
                                 <td className="q-align-left">Total</td>
                                 <td className="q-align-right q-num">
                                   {displayMoney(taxTotals.taxableValue ?? summary.subtotal ?? 0)}
                                 </td>
                                 <td></td>
                                 <td className="q-align-right q-num">
                                   {displayMoney(taxTotals.centralAmount ?? 0)}
                                 </td>
                                 <td></td>
                                 <td className="q-align-right q-num">
                                   {displayMoney(taxTotals.stateAmount ?? 0)}
                                 </td>
                                 <td></td>
                                 <td className="q-align-right q-num">
                                   {displayMoney(taxTotals.igstAmount ?? 0)}
                                 </td>
                                 <td className="q-align-right q-num">
                                   {displayMoney(taxTotals.totalTax ?? summary.totalTax ?? 0)}
                                 </td>
                               </tr>
                             </tfoot>
                           )}
                         </table>
                       </div>
                     )}

                     {/* Section 4: Bank Details */}
                     {page.showBankDetails && (
                       <div className="section-bank-details q-box q-panel-body">
                         <p className="q-section-title text-gray-800">
                           Bank Details
                         </p>
                         <div className="q-panel-lines q-note text-gray-700">
                           <p>
                             <span className="font-semibold text-gray-900">Bank:</span>{' '}
                             {bankDetails.bankName || '—'}
                           </p>
                           <p>
                             <span className="font-semibold text-gray-900">Account #:</span>{' '}
                             {bankDetails.accountNumber || '—'}
                           </p>
                           <p>
                             <span className="font-semibold text-gray-900">IFSC Code:</span>{' '}
                             {bankDetails.ifsc || '—'}
                           </p>
                           <p>
                             <span className="font-semibold text-gray-900">Branch:</span>{' '}
                             {bankDetails.branch || '—'}
                           </p>
                         </div>
                       </div>
                     )}

                     {/* Section 5: Terms & Conditions */}
                     {page.showTerms && (
                       <div className="section-terms q-box q-panel-body">
                         <p className="q-section-title text-gray-800">
                           Terms, Conditions & License Limitation Clauses
                         </p>
                         {termsHtml ? (
                           <div
                             className="q-panel-lines ql-editor q-note text-gray-700"
                             style={{ padding: 0 }}
                             dangerouslySetInnerHTML={{ __html: termsHtml }}
                           />
                         ) : (
                           <p className="q-note text-gray-500" style={{ marginTop: 6 }}>
                             Terms & conditions will be listed here once provided.
                           </p>
                         )}
                       </div>
                     )}

                     {/* Section 6: Representatives */}
                     {page.showRepresentatives && (
                       <div className="section-representatives grid gap-4 md:grid-cols-2">
                         <div className="q-box q-panel-body">
                           <p className="q-section-title text-gray-800">
                             Vendor Representative
                           </p>
                           <div className="q-panel-lines q-note text-gray-700">
                             <p className="font-semibold text-gray-900">
                               {vendorInfo.representative || meta.vendorPoc?.name || '—'}
                             </p>
                             <p>{meta.vendorPoc?.role || vendorInfo.role || '—'}</p>
                             <div className="q-signature-row">
                               {meta.vendorSign ? (
                                 <>
                                   <CheckCircle className="text-green-500" />
                                   <span className="font-semibold text-green-700">Digitally Signed</span>
                                 </>
                               ) : (
                                 <span className="text-gray-500">Signature pending</span>
                               )}
                             </div>
                           </div>
                         </div>
                         <div className="q-box q-panel-body">
                           <p className="q-section-title text-gray-800">
                             Customer Representative
                           </p>
                           <div className="q-panel-lines q-note text-gray-700">
                             <p className="font-semibold text-gray-900">
                               {customerInfo.representative || meta.clientPoc?.name || '—'}
                             </p>
                             <p>{meta.clientPoc?.role || customerInfo.role || '—'}</p>
                             <div className="q-signature-row">
                               {meta.clientSign ? (
                                 <>
                                   <CheckCircle className="text-green-500" />
                                   <span className="font-semibold text-green-700">Digitally Signed</span>
                                 </>
                               ) : (
                                 <span className="text-gray-500">Signature pending</span>
                               )}
                             </div>
                           </div>
                         </div>
                       </div>
                     )}

                    </div>

                    <div className="pdf-page-bottom">
                      {isLastPage && (
                        <div className="q-powered-by-block">
                          <p className="q-powered-by-text">{poweredByText}</p>
                          <div className="q-powered-by-line" aria-hidden="true" />
                        </div>
                      )}
                      <div className="pdf-page-footer">
                        Page {page.pageNumber} of {estimatedPages}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="pdf-page bg-white shadow-2xl p-8" style={{ width: `${A4_WIDTH_PX}px`, minHeight: `${A4_HEIGHT_PX}px` }}>
              <div className="text-center text-gray-500">Loading preview...</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EstimationQuotationPrint;
